import { useFlag } from '@/components/posthog-provider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession } from '@/lib/session';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import {
  useNormalizeReviewLink,
  useReviewLink,
  useSaveReviewLink,
} from '../hooks/use-review-link';

export function ReviewLinkSettings() {
  const invitationsEnabled = useFlag('rollout-post-treatment-reviews');
  const settings = useReviewLink();
  const { data: session } = useSession();
  const canManage = session?.user?.orgRole === 'owner';
  const [draft, setDraft] = useState<string | null>(null);
  const [checked, setChecked] = useState<string | null>(null);
  const normalize = useNormalizeReviewLink();
  const save = useSaveReviewLink(() => {
    setDraft(null);
    setChecked(null);
    normalize.reset();
  });
  const current = settings.data?.reviewUrl ?? '';
  const value = draft ?? current;
  const busy = normalize.isPending || save.isPending;
  const preview = checked ?? (draft === null ? current : '');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          Post-treatment reviews
        </CardTitle>
        <CardDescription>
          A little thank-you. An invitation to share their experience.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {settings.isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading review settings…
          </p>
        ) : settings.isError ? (
          <div role="alert" className="space-y-2">
            <p>We couldn’t load your review link.</p>
            <Button variant="outline" onClick={() => void settings.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="max-w-xl space-y-4">
            <div className="space-y-2">
              <Label htmlFor="google-review-link">Google review link</Label>
              <p
                id="google-review-help"
                className="text-sm text-muted-foreground"
              >
                Paste your clinic’s link. We’ll tidy it up so customers land on
                the right review page.
              </p>
              <Input
                id="google-review-link"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder="https://g.page/r/…/review"
                aria-describedby="google-review-help"
                value={value}
                disabled={!canManage || busy}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setChecked(null);
                  normalize.reset();
                }}
              />
            </div>
            {normalize.isError && (
              <p role="alert" className="text-sm text-destructive">
                {normalize.error instanceof Error
                  ? normalize.error.message
                  : 'We couldn’t check that link. Please try again.'}
              </p>
            )}
            {preview && (
              <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
                <p className="text-sm font-medium">
                  {checked
                    ? 'Check this is your clinic before saving'
                    : 'Your review page'}
                </p>
                <a
                  className="flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
                  href={preview}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Google review page
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                <p className="break-all text-xs text-muted-foreground">
                  {preview}
                </p>
              </div>
            )}
            {canManage && (
              <div className="flex flex-wrap gap-2">
                {checked ? (
                  <Button disabled={busy} onClick={() => save.mutate(checked)}>
                    {save.isPending ? 'Saving…' : 'Save review link'}
                  </Button>
                ) : (
                  <Button
                    disabled={
                      busy || !value.trim() || (draft === null && !!current)
                    }
                    onClick={() =>
                      normalize.mutate(value, {
                        onSuccess: (result) => setChecked(result.reviewUrl),
                      })
                    }
                  >
                    {normalize.isPending ? 'Checking link…' : 'Check link'}
                  </Button>
                )}
                {!!current && (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => save.mutate(null)}
                  >
                    Remove link
                  </Button>
                )}
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {invitationsEnabled
                ? 'We send a branded review invitation as soon as a treatment is marked completed, to customers who have agreed to email contact.'
                : 'This link will be used when automatic review invitations are enabled for your organisation.'}
            </p>
            {!canManage && (
              <p className="text-sm text-muted-foreground">
                Only the organisation owner can change the review link.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
